import { useMemo } from "react";
import { useField } from "formik";

/**
 * Bind a Formik field storing an array of IDs (e.g. `category_ids`) to a
 * react-select multi-select. Returns everything the select needs plus the
 * touched/error state so the caller doesn't have to wire it by hand.
 */
export const useMultiIdField = (name, options) => {
  const [, meta, helpers] = useField(name);

  const value = useMemo(() => {
    const current = Array.isArray(meta.value) ? meta.value : [];
    if (!current.length) return [];
    // Preserve option order for stable rendering.
    return options.filter((o) => current.includes(o.value));
  }, [meta.value, options]);

  const onChange = (selected) => {
    const next = Array.isArray(selected)
      ? selected.map((o) => Number(o.value))
      : [];
    helpers.setValue(next);
  };

  const onBlur = () => helpers.setTouched(true);

  const showError = meta.touched && meta.error;

  return { value, onChange, onBlur, error: showError ? meta.error : null, isInvalid: !!showError };
};

/**
 * Bind a Formik field storing a single ID (or null) to a react-select.
 */
export const useSingleIdField = (name, options) => {
  const [, meta, helpers] = useField(name);

  const value = useMemo(() => {
    if (meta.value === null || meta.value === undefined || meta.value === "") {
      return null;
    }
    return options.find((o) => o.value === meta.value) ?? null;
  }, [meta.value, options]);

  const onChange = (selected) => {
    helpers.setValue(selected ? Number(selected.value) : null);
  };

  const onBlur = () => helpers.setTouched(true);

  const showError = meta.touched && meta.error;

  return { value, onChange, onBlur, error: showError ? meta.error : null, isInvalid: !!showError };
};
